import { db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import authService from './authService';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';

/**
 * User Administration Service (Firestore-backed)
 * Note: Firestore doesn't support server-side joins; queries fetch collections and then join client-side.
 */

const collectionRef = (name) => collection(db, name);

// Helper to fetch collection ordered
const fetchCollection = async (name, orderField = 'created_at', direction = 'desc') => {
  const col = collection(db, name);
  const q = query(col, orderBy(orderField, direction === 'desc' ? 'desc' : 'asc'));
  const snaps = await getDocs(q);
  return snaps.docs.map(d => d.data());
};

export const getAllUsers = async () => {
  try {
    const usersSnaps = await getDocs(collectionRef('user_profiles'));
    const users = usersSnaps.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => (b.created_at || '') > (a.created_at || '') ? 1 : -1);

    if (!users || users.length === 0) return { data: [], error: null };

    // Try to fetch staff assignments - but don't fail if collection doesn't exist
    let assignments = [];
    try {
      const assignmentsSnaps = await getDocs(collectionRef('staff_assignments'));
      assignments = assignmentsSnaps.docs.map(d => d.data());
    } catch (assignError) {
      console.warn('Could not fetch staff_assignments:', assignError.message);
    }

    // Try to fetch departments - but don't fail if collection doesn't exist
    let deptMap = {};
    try {
      const departmentsSnaps = await getDocs(collectionRef('departments'));
      const departments = departmentsSnaps.docs.map(d => d.data());
      departments.forEach(d => { if (d.id) deptMap[d.id] = d; });
    } catch (deptError) {
      console.warn('Could not fetch departments:', deptError.message);
    }

    const usersWithAssignments = users.map(user => ({
      ...user,
      staff_assignments: assignments.filter(a => a.user_id === user.id).map(a => ({
        ...a,
        departments: deptMap[a.department_id] || null
      }))
    }));

    return { data: usersWithAssignments, error: null };
  } catch (error) {
    console.error('Error in getAllUsers:', error);
    return { data: null, error: error?.message || 'Missing or insufficient permissions.' };
  }
};

// Get users by role
export const getUsersByRole = async (role) => {
  try {
    const q = query(collectionRef('user_profiles'), where('role', '==', role));
    const snaps = await getDocs(q);
    const users = snaps.docs.map(d => d.data()).sort((a, b) => (b.created_at || '') > (a.created_at || '') ? 1 : -1);
    if (!users || users.length === 0) return { data: [], error: null };

    const assignmentsSnaps = await getDocs(collectionRef('staff_assignments'));
    const assignments = assignmentsSnaps.docs.map(d => d.data());

    const usersWithAssignments = users.map(user => ({
      ...user,
      staff_assignments: assignments.filter(a => a.user_id === user.id) || []
    }));

    return { data: usersWithAssignments, error: null };
  } catch (error) {
    console.error('Error in getUsersByRole:', error);
    return { data: null, error: error?.message };
  }
};

// Get users by employment status
export const getUsersByStatus = async (status) => {
  try {
    const q = query(collectionRef('user_profiles'), where('employment_status', '==', status));
    const snaps = await getDocs(q);
    const users = snaps.docs.map(d => d.data()).sort((a, b) => (b.created_at || '') > (a.created_at || '') ? 1 : -1);
    if (!users || users.length === 0) return { data: [], error: null };

    const assignmentsSnaps = await getDocs(collectionRef('staff_assignments'));
    const assignments = assignmentsSnaps.docs.map(d => d.data());

    const usersWithAssignments = users.map(user => ({
      ...user,
      staff_assignments: assignments.filter(a => a.user_id === user.id) || []
    }));

    return { data: usersWithAssignments, error: null };
  } catch (error) {
    console.error('Error in getUsersByStatus:', error);
    return { data: null, error: error?.message };
  }
};

// Get users by department - using staff_assignments as the primary table
export const getUsersByDepartment = async (departmentId) => {
  try {
    const q = query(collectionRef('staff_assignments'), where('department_id', '==', departmentId));
    const snaps = await getDocs(q);
    const assignments = snaps.docs.map(d => d.data()).sort((a, b) => (b.assigned_at || '') > (a.assigned_at || '') ? 1 : -1);

    // Fetch user profiles referenced
    const userIds = assignments.map(a => a.user_id);
    const usersSnaps = await getDocs(query(collectionRef('user_profiles'), where('id', 'in', userIds)));
    const users = usersSnaps.docs.map(d => d.data());

    // Fetch department
    const deptSnap = await getDoc(doc(db, 'departments', departmentId));
    const deptData = deptSnap.exists() ? deptSnap.data() : null;

    // Attach user profile to assignments
    const result = assignments.map(a => ({
      ...a,
      user_profiles: users.find(u => u.id === a.user_id) || null,
      departments: deptData
    }));

    return { data: result, error: null };
  } catch (error) {
    console.error('Error in getUsersByDepartment:', error);
    return { data: null, error: error?.message };
  }
};

// Create new user profile
export const createUser = async (userData) => {
  try {
    // if id provided, set with that id
    if (userData?.id) {
      await setDoc(doc(db, 'user_profiles', userData.id), userData);
      const snap = await getDoc(doc(db, 'user_profiles', userData.id));
      return { data: snap.data(), error: null };
    }
    const ref = await addDoc(collectionRef('user_profiles'), userData);
    const snap = await getDoc(ref);
    return { data: snap.data(), error: null };
  } catch (error) {
    console.error('Error in createUser:', error);
    return { data: null, error: error?.message };
  }
};

// Update user profile
export const updateUser = async (userId, updates) => {
  try {
    // Validate userId
    if (!userId) {
      return {
        data: null,
        error: 'User ID is required for update operation.'
      };
    }

    // First check if user exists to provide better error handling
    const existingSnap = await getDoc(doc(db, 'user_profiles', userId));
    const existingUser = existingSnap.exists() ? existingSnap.data() : null;

    if (!existingUser) {
      return { data: null, error: `User with ID ${userId} not found.` };
    }

    // Remove undefined values and ensure we're only updating allowed fields
    const cleanUpdates = {};
    Object.entries(updates)?.forEach(([key, value]) => {
      // Special handling for fields that allow null to clear values
      if (key === 'department' || key === 'photo_url') {
        cleanUpdates[key] = value || null;
      } else if (value !== undefined && value !== null && value !== '') {
        cleanUpdates[key] = value;
      }
    });

    // Add updated timestamp
    cleanUpdates.updated_at = new Date().toISOString();

    // Perform update
    await updateDoc(doc(db, 'user_profiles', userId), cleanUpdates);
    const updatedSnap = await getDoc(doc(db, 'user_profiles', userId));
    return { data: updatedSnap.data(), error: null };
  } catch (error) {
    console.error('Exception in updateUser:', error);
    return { data: null, error: error?.message || 'Failed to update user profile' };
  }
};

// Update user status
export const updateUserStatus = async (userId, status) => {
  try {
    const snap = await getDoc(doc(db, 'user_profiles', userId));
    if (!snap.exists()) return { data: null, error: `User with ID ${userId} not found or access denied.` };
    await updateDoc(doc(db, 'user_profiles', userId), { employment_status: status, updated_at: new Date().toISOString() });
    const updatedSnap = await getDoc(doc(db, 'user_profiles', userId));
    return { data: updatedSnap.data(), error: null };
  } catch (error) {
    console.error('Error in updateUserStatus:', error);
    return { data: null, error: error?.message || 'Failed to update user status' };
  }
};

// Assign user to department
export const assignUserToDepartment = async (userId, departmentId, assignedBy) => {
  try {
    // Validate required parameters
    if (!userId) {
      return { data: null, error: 'User ID is required for department assignment' };
    }
    if (!departmentId) {
      return { data: null, error: 'Department ID is required for department assignment' };
    }

    const ref = await addDoc(collectionRef('staff_assignments'), {
      user_id: userId,
      department_id: departmentId,
      assigned_by: assignedBy || userId,
      is_primary: true,
      assigned_at: new Date().toISOString()
    });
    const snap = await getDoc(ref);
    return { data: snap.data(), error: null };
  } catch (error) {
    console.error('Error in assignUserToDepartment:', error);
    return { data: null, error: error?.message };
  }
};

// Remove user from department
export const removeUserFromDepartment = async (userId, departmentId) => {
  try {
    const q = query(collectionRef('staff_assignments'), where('user_id', '==', userId), where('department_id', '==', departmentId));
    const snaps = await getDocs(q);
    const results = [];
    for (const d of snaps.docs) {
      await deleteDoc(doc(db, 'staff_assignments', d.id));
      results.push(d.data());
    }
    return { data: results, error: null };
  } catch (error) {
    console.error('Error in removeUserFromDepartment:', error);
    return { data: null, error: error?.message };
  }
};

// Get all departments
export const getAllDepartments = async () => {
  try {
    const snaps = await getDocs(query(collectionRef('departments'), orderBy('name', 'asc')));
    const data = snaps.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
    return { data, error: null };
  } catch (error) {
    console.error('Error in getAllDepartments:', error);
    return { data: null, error: error?.message };
  }
};

// Create a new department
export const createDepartment = async (departmentData) => {
  try {
    const ref = await addDoc(collectionRef('departments'), {
      ...departmentData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
    const snap = await getDoc(ref);
    return { data: { id: snap.id, ...snap.data() }, error: null };
  } catch (error) {
    console.error('Error in createDepartment:', error);
    return { data: null, error: error?.message };
  }
};

// Update a department
export const updateDepartment = async (departmentId, departmentData) => {
  try {
    const ref = doc(db, 'departments', departmentId);
    await updateDoc(ref, {
      ...departmentData,
      updated_at: new Date().toISOString()
    });
    const snap = await getDoc(ref);
    return { data: { id: snap.id, ...snap.data() }, error: null };
  } catch (error) {
    console.error('Error in updateDepartment:', error);
    return { data: null, error: error?.message };
  }
};

// Delete a department
export const deleteDepartment = async (departmentId) => {
  try {
    await deleteDoc(doc(db, 'departments', departmentId));
    return { data: true, error: null };
  } catch (error) {
    console.error('Error in deleteDepartment:', error);
    return { data: null, error: error?.message };
  }
};

// Get role permissions for a specific role
export const getRolePermissions = async (role) => {
  try {
    const snaps = await getDocs(query(collectionRef('role_permissions'), where('role', '==', role)));
    const data = snaps.docs.map(d => d.data());
    return { data, error: null };
  } catch (error) {
    console.error('Error in getRolePermissions:', error);
    return { data: null, error: error?.message };
  }
};

// Update role permissions
export const updateRolePermissions = async (role, module, permissions) => {
  try {
    // Use doc id as `${role}_${module}` for upsert-like behavior
    const id = `${role}_${module}`;
    await setDoc(doc(db, 'role_permissions', id), { role, module, ...permissions });
    const snap = await getDoc(doc(db, 'role_permissions', id));
    return { data: snap.data(), error: null };
  } catch (error) {
    console.error('Error in updateRolePermissions:', error);
    return { data: null, error: error?.message };
  }
};

// Fetch ALL role_permissions docs in a single query (for AuthContext caching)
export const getAllRolePermissions = async () => {
  try {
    const snaps = await getDocs(collectionRef('role_permissions'));
    const data = snaps.docs.map(d => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (error) {
    console.error('Error in getAllRolePermissions:', error);
    return { data: null, error: error?.message };
  }
};

// Fetch permissions for a specific role (convenience wrapper)
export const getPermissionsForRole = async (role) => {
  try {
    const snaps = await getDocs(query(collectionRef('role_permissions'), where('role', '==', role)));
    const data = snaps.docs.map(d => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (error) {
    console.error('Error in getPermissionsForRole:', error);
    return { data: null, error: error?.message };
  }
};

// Get user statistics
export const getUserStatistics = async () => {
  try {
    // Get all user profiles
    const snaps = await getDocs(collectionRef('user_profiles'));
    const allUsers = snaps.docs.map(d => d.data());

    // Filter active users (if employment_status exists, check it; otherwise consider active)
    const activeUsers = allUsers.filter(u => !u.employment_status || u.employment_status === 'active');

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentUsers = allUsers.filter(u => u.created_at && new Date(u.created_at) >= thirtyDaysAgo);

    const roleCounts = {};
    activeUsers.forEach(user => {
      if (user.role) {
        roleCounts[user.role] = (roleCounts[user.role] || 0) + 1;
      }
    });

    return {
      data: {
        roleStats: roleCounts,
        totalActive: activeUsers.length || 0,
        recentRegistrations: recentUsers.length || 0
      },
      error: null
    };
  } catch (error) {
    console.error('Error in getUserStatistics:', error);
    return { data: null, error: error?.message };
  }
};

// Search users by multiple criteria
export const searchUsers = async (searchTerm, filters = {}) => {
  try {
    // Fetch all users and filter client-side (Firestore text search is limited)
    const snaps = await getDocs(collectionRef('user_profiles'));
    let users = snaps.docs.map(d => ({ ...d.data(), id: d.id }));

    // Filter out patient records - they have their own tab
    users = users.filter(u => u.role !== 'patient');

    // Apply text search (simple case-insensitive contains)
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      users = users.filter(u => (
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.employee_id || '').toLowerCase().includes(q)
      ));
    }

    // Apply filters
    if (filters.role) users = users.filter(u => u.role === filters.role);
    if (filters.employment_status) users = users.filter(u => u.employment_status === filters.employment_status);

    if (!users || users.length === 0) return { data: [], error: null };

    // Department filter
    if (filters.department) {
      const assignSnaps = await getDocs(query(collectionRef('staff_assignments'), where('department_id', '==', filters.department)));
      const userIdsInDept = assignSnaps.docs.map(d => d.data().user_id);
      users = users.filter(u => userIdsInDept.includes(u.id));
    }

    // Attach assignments
    const userIds = users.map(u => u.id).filter(Boolean);
    let assignments = [];
    if (userIds.length > 0) {
      // Firestore 'in' queries support max 30 items, batch if needed
      const batches = [];
      for (let i = 0; i < userIds.length; i += 30) {
        batches.push(userIds.slice(i, i + 30));
      }
      for (const batch of batches) {
        const assignSnaps = await getDocs(query(collectionRef('staff_assignments'), where('user_id', 'in', batch)));
        assignments.push(...assignSnaps.docs.map(d => d.data()));
      }
    }

    const usersWithAssignments = users.map(user => ({
      ...user,
      staff_assignments: assignments.filter(a => a.user_id === user.id) || []
    }));

    return { data: usersWithAssignments, error: null };
  } catch (error) {
    console.error('Error in searchUsers:', error);
    return { data: null, error: error?.message };
  }
};

// Create a new user invitation
export const createInvitation = async (invitationData) => {
  try {
    // Check if invitation already exists for this email
    const q = query(collectionRef('invitations'), where('email', '==', invitationData.email), where('status', '==', 'pending'));
    const snaps = await getDocs(q);

    if (!snaps.empty) {
      // Update existing invitation
      const docId = snaps.docs[0].id;
      await updateDoc(doc(db, 'invitations', docId), {
        ...invitationData,
        updated_at: new Date().toISOString()
      });
      return { data: { id: docId, ...invitationData }, error: null };
    }

    // Create new invitation
    const ref = await addDoc(collectionRef('invitations'), {
      ...invitationData,
      created_at: new Date().toISOString(),
      status: 'pending'
    });

    return { data: { id: ref.id, ...invitationData }, error: null };
  } catch (error) {
    console.error('Error in createInvitation:', error);
    return { data: null, error: error?.message };
  }
};

// Validate invitation token
export const validateInvitation = async (token) => {
  try {
    const q = query(collectionRef('invitations'), where('token', '==', token));
    const snaps = await getDocs(q);

    if (snaps.empty) {
      return { data: null, error: 'Invalid invitation token' };
    }

    const inviteDoc = snaps.docs[0];
    const inviteData = inviteDoc.data();

    // Check expiry
    if (new Date(inviteData.expiresAt) < new Date()) {
      return { data: null, error: 'Invitation has expired' };
    }

    // Check status
    if (inviteData.status !== 'pending') {
      return { data: null, error: 'Invitation has already been used' };
    }

    return { data: { id: inviteDoc.id, ...inviteData }, error: null };
  } catch (error) {
    console.error('Error in validateInvitation:', error);
    return { data: null, error: error?.message };
  }
};

// Mark invitation as accepted
export const acceptInvitation = async (invitationId) => {
  try {
    await updateDoc(doc(db, 'invitations', invitationId), {
      status: 'accepted',
      accepted_at: new Date().toISOString()
    });
    return { data: true, error: null };
  } catch (error) {
    console.error('Error in acceptInvitation:', error);
    return { data: null, error: error?.message };
  }
};

// Get all invitations
export const getAllInvitations = async () => {
  try {
    const snaps = await getDocs(query(collectionRef('invitations'), orderBy('created_at', 'desc')));
    const data = snaps.docs.map(d => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (error) {
    console.error('Error in getAllInvitations:', error);
    return { data: null, error: error?.message };
  }
};

// Cancel/Delete invitation
export const cancelInvitation = async (invitationId) => {
  try {
    await deleteDoc(doc(db, 'invitations', invitationId));
    return { data: true, error: null };
  } catch (error) {
    console.error('Error in cancelInvitation:', error);
    return { data: null, error: error?.message };
  }
};

// ==================== ROLE MANAGEMENT ====================

// Get all roles
export const getAllRoles = async () => {
  try {
    const snaps = await getDocs(collectionRef('roles'));
    const data = snaps.docs.map(d => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (error) {
    console.error('Error in getAllRoles:', error);
    return { data: null, error: error?.message };
  }
};

// Create a new role
export const createRole = async (roleData) => {
  try {
    const ref = await addDoc(collectionRef('roles'), {
      ...roleData,
      created_at: new Date().toISOString()
    });
    return { data: { id: ref.id, ...roleData }, error: null };
  } catch (error) {
    console.error('Error in createRole:', error);
    return { data: null, error: error?.message };
  }
};

// Update a role
export const updateRole = async (roleId, roleData) => {
  try {
    await updateDoc(doc(db, 'roles', roleId), {
      ...roleData,
      updated_at: new Date().toISOString()
    });
    return { data: true, error: null };
  } catch (error) {
    console.error('Error in updateRole:', error);
    return { data: null, error: error?.message };
  }
};

// Delete a role
export const deleteRole = async (roleId) => {
  try {
    await deleteDoc(doc(db, 'roles', roleId));
    return { data: true, error: null };
  } catch (error) {
    console.error('Error in deleteRole:', error);
    return { data: null, error: error?.message };
  }
};

// Admin send password reset email to user
export const adminSendResetEmail = async (email) => {
  return await authService.resetPassword(email);
};

// Admin directly set user's password
export const adminSetUserPassword = async (userId, newPassword) => {
  try {
    if (!userId || !newPassword) {
      return { data: null, error: 'User ID and new password are required' };
    }
    if (newPassword.length < 6) {
      return { data: null, error: 'Password must be at least 6 characters' };
    }

    const resetCallable = httpsCallable(functions, 'adminResetUserPassword');
    const result = await resetCallable({ userId, newPassword });
    return { data: result.data, error: null };
  } catch (error) {
    console.error('Error in adminSetUserPassword:', error);
    return { data: null, error: error?.message || 'Failed to update user password' };
  }
};

// Admin suspend / unsuspend user account
export const adminToggleUserSuspension = async (userId, suspend, reason = '') => {
  try {
    if (!userId) {
      return { data: null, error: 'User ID is required' };
    }

    // Try calling Cloud Function to disable user in Firebase Auth and Firestore
    try {
      const toggleCallable = httpsCallable(functions, 'adminToggleUserSuspension');
      const result = await toggleCallable({ userId, suspend, reason });
      return { data: result.data, error: null };
    } catch (cfError) {
      console.warn('Cloud Function toggle suspension failed, applying direct Firestore update:', cfError.message);
      // Fallback directly to Firestore so the user is immediately marked suspended and logged out via real-time listener
      const status = suspend ? 'suspended' : 'active';
      const userRef = doc(db, 'user_profiles', userId);
      await updateDoc(userRef, {
        employment_status: status,
        is_suspended: suspend,
        suspension_reason: suspend ? (reason || 'Account suspended by administrator') : null,
        suspended_at: suspend ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      });
      return { data: { success: true, status }, error: null };
    }
  } catch (error) {
    console.error('Error in adminToggleUserSuspension:', error);
    return { data: null, error: error?.message || 'Failed to toggle account suspension' };
  }
};

export default {
  getAllUsers,
  getUsersByRole,
  getUsersByStatus,
  getUsersByDepartment,
  createUser,
  updateUser,
  updateUserStatus,
  assignUserToDepartment,
  removeUserFromDepartment,
  getAllDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  getRolePermissions,
  updateRolePermissions,
  getAllRolePermissions,
  getPermissionsForRole,
  getUserStatistics,
  searchUsers,
  createInvitation,
  validateInvitation,
  acceptInvitation,
  getAllInvitations,
  cancelInvitation,
  getAllRoles,
  createRole,
  updateRole,
  deleteRole,
  adminSendResetEmail,
  adminSetUserPassword,
  adminToggleUserSuspension
};